<template>
  <div
    :id="elId"
    class="radar-grid-object-popup-container"
    :class="{
      'is-pinned-to-screen': isPinnedToScreen,
      unmonitored: !isMonitored,
    }"
    @mousedown="mouseDownHandler"
    :style="styleObject"
    ref="radarGridObjectPopupContainer"
  >
    <span
      class="close-btn"
      @click="onClickCloseBtn"
      @mousedown.stop.prevent=""
    ></span>
    <span class="rg-pin-icon" @click.stop.prevent="handleClickPinButton"></span>
    <div class="loader-wrap max-h-full" v-show="loading">
      <div class="loader"></div>
    </div>
    <div class="rg-img" v-show="!loading">
      <img
        class="camera-image"
        :src="src"
        @load="load"
        v-show="hasImgUrl && !error"
      />
      <div class="no-image-text-wrap h-full" v-show="!hasImgUrl && !error">
        <div class="no-image-text aisea-text-10-400-white">
          画像はありません
        </div>
      </div>
      <div v-show="error" class="rg-img-error h-full">
        <span class="rg-img-error-text aisea-text-10-400-white">{{
          errorMsg
        }}</span>
      </div>
    </div>
    <div class="rg-info">
      <div class="rg-info-content">
        <div class="rg-info-content-row rg-info-recognition-rate">
          <div class="rg-info-content-header">物体種別</div>
          <div
            id="rg-info-recognition-rate-val"
            class="aisea-text-14-400-white h-6"
          >
            {{ objectRecognitionRate }}
          </div>
        </div>
        <div class="rg-info-content-row rg-info-speed">
          <div class="rg-info-content-header">速度</div>
          <div id="rg-info-speed-val" class="aisea-text-14-400-white">
            {{ sogForDisplay }}kn
          </div>
        </div>
        <div class="rg-info-content-row rg-info-direction">
          <div class="rg-info-content-header">針路</div>
          <div id="rg-info-direction-val" class="aisea-text-14-400-white">
            {{ cogForDisplay }}˚
          </div>
        </div>
        <div class="rg-info-content-row rg-info-latitude">
          <div class="rg-info-content-header">緯度</div>
          <div id="rg-info-latitude-val" class="aisea-text-14-400-white">
            {{ latitudeForDisplay }}
          </div>
        </div>
        <div class="rg-info-content-row rg-info-longitude">
          <div class="rg-info-content-header">経度</div>
          <div id="rg-info-longitude-val" class="aisea-text-14-400-white">
            {{ longitudeForDisplay }}
          </div>
        </div>
        <div class="rg-info-content-row rg-info-date">
          <div class="rg-info-content-header">撮影日時</div>
          <div id="rg-info-date-val" class="aisea-text-14-400-white">
            {{ shootingDate }}
          </div>
        </div>
        <div class="rg-info-content-row" v-if="riskType !== 'NONE'">
          <div class="rg-info-content-header">自動判定</div>
          <div class="risk-label" :style="{ color: riskLevelStatusObject.riskColor }">
            {{ riskLevelStatusObject.riskLabel }}
          </div>
        </div>
      </div>
    </div>
    <div class="rg-feb">
      <div class="rg-track-content">
        <div
          class="track-ic"
          @click="onClickTrackBtn(this)"
          @mousedown.stop.prevent=""
        >
          <div class="icon-stack" v-if="riskLevelStatusObject.trackIcon.length">
            <img :src="riskLevelStatusObject.trackIcon[0]" class="rg-detail-status-icon bg-icon" alt="icon-bg" />
            <img v-if="riskLevelStatusObject.trackIcon[1]" :src="riskLevelStatusObject.trackIcon[1]" class="rg-detail-status-icon overlay-icon" alt="icon-overlay" />
          </div>
        </div>
        <div
          class="track-number aisea-text-12-400-white"
          :style="riskLevelStatusObject.trackNumber"
        >
          {{ trackNumber }}
        </div>
      </div>
      <div class="rg-risk-level-content" v-if="riskType !== 'NONE'">
        <span class="rg-feb-status-danger" :style="{ opacity: riskType === 'DANGER' ? 1 : 0.3 }"
          @mouseenter="showTooltip('danger')" @mouseleave="hideTooltip">
          <img :src="require('@/assets/images/radarGrid/feedback_danger.svg')" alt="Danger"/>
          <div v-if="tooltip === 'danger'" class="rg-tooltip" :style="{ color: '#EB5757' }">
            危険
          </div>
        </span>
        <span class="rg-feb-status-caution" :style="{ opacity: riskType === 'CAUTION' ? 1 : 0.3 }"
          @mouseenter="showTooltip('caution')" @mouseleave="hideTooltip">
          <img :src="require('@/assets/images/radarGrid/feedback_caution.svg')" alt="Caution" />
          <div v-if="tooltip === 'caution'" class="rg-tooltip" :style="{ color: '#EFF335' }">
            注意
          </div>
        </span>
        <span class="rg-feb-status-safe" :style="{ opacity: riskType === 'SAFE' ? 1 : 0.3 }"
          @mouseenter="showTooltip('safe')" @mouseleave="hideTooltip">
          <img :src="require('@/assets/images/radarGrid/feedback_safe.svg')" alt="Safe" />
          <div v-if="tooltip === 'safe'" class="rg-tooltip" :style="{ color: '#18B75C' }">
            安全
          </div>
        </span>
      </div>
      <div class="rg-risk-assessment"></div>
    </div>
    <template v-if="isMonitored">
      <dialog-input-reason-risk-level
        v-if="tmpSelectDanger"
        v-bind="PropsDialogInputRiskLevel"
      ></dialog-input-reason-risk-level>
      <dialog-input-risk-level
        v-else-if="tmpSelectCautionOrSafeOrCancel"
        v-bind="PropsDialogInputRiskLevel"
      ></dialog-input-risk-level>
    </template>
  </div>
</template>

<script>
import { RadarGridMixin } from "@/mixins";
import { radarGridStorageRef } from "@/firebase/firebaseConfig";
import Vue from "vue";
import throttle from "lodash/throttle";
import isString from "lodash/isString";
import isNumber from "lodash/isNumber";
import debounce from "lodash/debounce";
const _ = {
  throttle,
  isString,
  isNumber,
  debounce,
};
import * as NumberUtil from "@/utils/NumberUtil";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { GeoUtil } from "@/utils";
import { RadarGridObjectUtil } from "../utils";
const FALLBACK_COORDINATES_FOR_DISPLAY = "---";

export default {
  name: "RgPopup",
  mixins: [RadarGridMixin],
  components: {},
  props: {
    objectId: String,
    elId: String,
    points: Object,
    radarGridCameraMaster: {
      type: Object,
      default: () => {
        return {};
      },
    },
    cameraHashMap: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      coordinates: null, // :[number, number] => [log, lat]
      trackNumber: String,
      cameraId: String,
      imgUrl: String,
      kind: String,
      confidence: null,
      sog: null,
      cog: null,
      riskLevel: Number,
      shootingDate: null,
      src: "",
      loading: false,
      error: false,
      errorMsg: "画像が取得できませんでした",
      styleObject: {
        top: "0px",
        left: "0px",
        position: "absolute",
      },
      disabledColor: "#979292",
      viewCameraPrivilegeMap: {},
      controlCameraPrivilegeMap: {},
      rateRiskPrivilegeMap: {},
      mapOfCameraIdByRadarId: {},
      radarGridInfo: [],
      tmpSelectRiskLevel: null,
      rgRiskLevelManagerMap: {},
      //zoomPicStubDoing: zoomPicStubDoingInit,
      isMonitored: true, // boolean
      tooltip: null,
    };
  },

  watch: {
    imgUrl: async function(newVal, oldVal) {
      if (newVal === oldVal || !this.hasImgUrl) return;
      await this.getImgUrl(this.imgUrl);
      this.$emit("onImgUrlChanged", this.objectId);
    },

    trackNumber(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$emit(
        "onChangePopupReactiveValues",
        this.transferPopupReactiveValues
      );
    },

    riskLevel(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$emit(
        "onChangePopupReactiveValues",
        this.transferPopupReactiveValues
      );
    },

    aiseaCameraName(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$emit(
        "onChangePopupReactiveValues",
        this.transferPopupReactiveValues
      );
    },

    confidence: function(newVal, oldVal) {
      if (oldVal === null) return;
      if (newVal !== oldVal) {
        this.blinkBackground("rg-info-recognition-rate-val");
      }
    },

    sog: function(newVal, oldVal) {
      if (oldVal === null) return;
      if (newVal !== oldVal) {
        this.blinkBackground("rg-info-speed-val");
      }
    },

    cog: function(newVal, oldVal) {
      if (oldVal === null) return;
      if (newVal !== oldVal) {
        this.blinkBackground("rg-info-direction-val");
      }
    },

    latitudeForDisplay(newVal, oldVal) {
      if (oldVal === FALLBACK_COORDINATES_FOR_DISPLAY) return;
      if (newVal !== oldVal) {
        this.blinkBackground("rg-info-latitude-val");
      }
    },

    longitudeForDisplay(newVal, oldVal) {
      if (oldVal === FALLBACK_COORDINATES_FOR_DISPLAY) return;
      if (newVal !== oldVal) {
        this.blinkBackground("rg-info-longitude-val");
      }
    },

    shootingDate(newVal, oldVal) {
      if (oldVal === null) return;
      if (newVal !== oldVal) {
        this.blinkBackground("rg-info-date-val");
      }
    },

    rgRiskLevelManager(val) {
      if (val) {
        this.riskLevel = val.riskLevel;
      }
    },
  },

  computed: {
    objectRecognitionRate() {
      const { kind, confidence } = this;
      return RadarGridObjectUtil.formatObjectRecognitionRate(kind, confidence);
    },
    cogForDisplay() {
      if (isNaN(this.cog)) return "";
      return NumberUtil.toTrunc(this.cog, 1).toString();
    },

    sogForDisplay() {
      if (isNaN(this.sog)) return "";
      return NumberUtil.toTrunc(this.sog, 1).toString();
    },

    hasCoordinates() {
      const { coordinates } = this;
      if (!Array.isArray(coordinates)) return false;
      return coordinates.length === 2 && coordinates.every(_.isNumber);
    },

    hasValidLatitude() {
      const latitude = this.getLatitude();
      return GeoUtil.isValidLatitude(latitude);
    },

    hasValidLongitude() {
      const longitude = this.getLongitude();
      return GeoUtil.isValidLongitude(longitude);
    },

    latitudeForDisplay() {
      if (!this.hasCoordinates) return FALLBACK_COORDINATES_FOR_DISPLAY;
      if (!this.hasValidLatitude || !this.hasValidLongitude) {
        return FALLBACK_COORDINATES_FOR_DISPLAY;
      }
      return `${GeoUtil.toSexagesimalScale(
        Math.abs(this.getLatitude())
      )}${this.calcLatitudePrefix()}`;
    },

    longitudeForDisplay() {
      if (!this.hasCoordinates) return FALLBACK_COORDINATES_FOR_DISPLAY;
      if (!this.hasValidLongitude || !this.hasValidLatitude) {
        return FALLBACK_COORDINATES_FOR_DISPLAY;
      }
      return `${GeoUtil.toSexagesimalScale(
        Math.abs(this.getLongitude())
      )}${this.calcLongitudePrefix()}`;
    },

    hasWriteRateRiskPrivilege() {
      return Object.prototype.hasOwnProperty.call(
        this.rateRiskPrivilegeMap,
        this.RG_PRIVILEGE_TYPES.WRITE
      );
    },

    hasCameraId() {
      return _.isString(this.cameraId) && this.cameraId !== "";
    },

    hasImgUrl() {
      return _.isString(this.imgUrl) && this.imgUrl !== "";
    },

    rgCameraId() {
      if (this.hasCameraId) return this.cameraId;
      if (!Array.isArray(this.radarGridIds)) return "";
      return this.radarGridIds.reduce((cameraId, radarId) => {
        if (_.isString(cameraId) && cameraId !== "") return cameraId;
        const setOfCameraId = this.mapOfCameraIdByRadarId[radarId];
        if (setOfCameraId) {
          const cameraIds = setOfCameraId.values();
          return cameraIds.next().value;
        }
        return cameraId;
      }, "");
    },

    radarCameraMaster() {
      return this.radarGridCameraMaster[this.rgCameraId] || null;
    },

    aiseaCameraHash() {
      if (!this.radarCameraMaster) return "";
      return this.radarCameraMaster.a || "";
    },

    aiseaCameraName() {
      if (!_.isString(this.aiseaCameraHash)) return "";
      if (!this.cameraHashMap) return "";
      if (!Object.hasOwn(this.cameraHashMap, this.aiseaCameraHash)) return "";
      return this.cameraHashMap[this.aiseaCameraHash].name || "";
    },

    radarGridIds() {
      if (!Array.isArray(this.radarGridInfo)) return [];
      return this.radarGridInfo
        .filter(radarGrid => radarGrid && radarGrid.r)
        .map(({ r }) => r);
    },

    isTrack() {
      return typeof this.kind === "string" && this.kind.length > 0 && this.kind !== "buoy" && this.kind !== "floating";
    },
    riskLevelStatusObject() {
      return this.createRiskLevelStatusLabel(this.riskLevel, this.isTrack);
    },
    riskType() {
      if (this.riskLevel >= 0 && this.riskLevel <= 2) return "SAFE";
      if (this.riskLevel >= 8 && this.riskLevel <= 10) return "DANGER";
      if (this.riskLevel >= 3 && this.riskLevel <= 7) return "CAUTION";
      return "NONE";
    },

    tmpSelectDanger() {
      return this.tmpSelectRiskLevel === this.RG_RISK_LEVEL.DANGER;
    },

    tmpSelectCautionOrSafeOrCancel() {
      return (
        this.tmpSelectCaution || this.tmpSelectSafe || this.tmpSelectCancel
      );
    },

    tmpSelectCaution() {
      return this.tmpSelectRiskLevel === this.RG_RISK_LEVEL.CAUTION;
    },

    tmpSelectSafe() {
      return this.tmpSelectRiskLevel === this.RG_RISK_LEVEL.SAFE;
    },

    tmpSelectCancel() {
      return this.tmpSelectRiskLevel === this.RG_RISK_LEVEL_CANCELED;
    },

    PropsDialogInputRiskLevel() {
      return {
        active: true,
        radarDetectedObjectId: this.objectId,
        riskLevel: this.tmpSelectRiskLevel,
        handleAccept: this.handleAcceptUpdateRiskLevel,
        handleCancel: this.resetTmpSelectRiskLevel,
        handleClose: this.resetTmpSelectRiskLevel,
      };
    },

    isPinnedToScreen() {
      return this.styleObject && this.styleObject.position === "fixed";
    },

    hasManualAssessment() {
      if (!this.rgRiskLevelManager) return false;
      return this.rgRiskLevelManager.hasManualAssessment;
    },

    autoRiskLevel() {
      if (!this.rgRiskLevelManager) return this.RG_RISK_LEVEL.CAUTION;
      return this.rgRiskLevelManager.autoRiskLevel;
    },

    rgRiskLevelManager() {
      return this.rgRiskLevelManagerMap[this.objectId];
    },
  },

  methods: {
    setData(properties) {
      this.coordinates = [properties.log, properties.lat];
      this.trackNumber = properties.trackNumber;
      this.cameraId = properties.cameraId;
      this.imgUrl = properties.imgUrl;
      this.kind = properties.k;
      this.confidence = properties.confidence;
      this.sog = properties.sog;
      this.cog = properties.cog;
      this.riskLevel = properties.riskLevel;
      this.shootingDate = properties.shootingDate;
    },

    setRadarGridPrivilegeMap({
      viewCameraPrivilegeMap,
      controlCameraPrivilegeMap,
      rateRiskPrivilegeMap,
    }) {
      this.setViewCameraPrivilegeMap(viewCameraPrivilegeMap);
      this.setControlCameraPrivilegeMap(controlCameraPrivilegeMap);
      this.setRateRiskPrivilegeMap(rateRiskPrivilegeMap);
    },

    setViewCameraPrivilegeMap(viewCameraPrivilegeMap) {
      if (viewCameraPrivilegeMap) {
        this.viewCameraPrivilegeMap = viewCameraPrivilegeMap;
      }
    },

    setControlCameraPrivilegeMap(controlCameraPrivilegeMap) {
      if (controlCameraPrivilegeMap) {
        this.controlCameraPrivilegeMap = controlCameraPrivilegeMap;
      }
    },

    setRateRiskPrivilegeMap(rateRiskPrivilegeMap) {
      if (rateRiskPrivilegeMap) {
        this.rateRiskPrivilegeMap = rateRiskPrivilegeMap;
      }
    },

    setError(msg) {
      this.error = true;
      this.errorMsg = msg;
    },

    /**
     * 監視状態を解除する
     */
    turnOffMonitoring() {
      this.isMonitored = false;
    },

    getLatitude() {
      if (!this.hasCoordinates) return null;
      return this.coordinates[1];
    },

    getLongitude() {
      if (!this.hasCoordinates) return null;
      return this.coordinates[0];
    },

    calcLatitudePrefix() {
      if (!this.hasValidLatitude) return "";
      if (GeoUtil.isNorthLatitude(this.getLatitude())) return "N";
      return "S";
    },

    calcLongitudePrefix() {
      if (!this.hasValidLongitude) return "";
      if (GeoUtil.isEastLongitude(this.getLongitude())) return "E";
      return "W";
    },

    async getImgUrl(path) {
      try {
        this.loading = true;
        this.src = await radarGridStorageRef.child(path).getDownloadURL();
        this.error = false;
      } catch (error) {
        Vue.$log.debug(error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    load() {
      this.loading = false;
    },

    mouseDownHandler(e) {
      this.x = e.clientX;
      this.y = e.clientY;
      document.addEventListener("mousemove", this.mouseMoveHandler);
      document.addEventListener("mouseup", this.mouseUpHandler);
    },

    mouseMoveHandler(e) {
      if (!this.isPinnedToScreen) {
        this.pinRgPopupToScreen();
      } else {
        const dx = e.clientX - this.x;
        const dy = e.clientY - this.y;
        const el = document.getElementById(this.elId);
        this.styleObject.top = `${el.offsetTop + dy}px`;
        this.styleObject.left = `${el.offsetLeft + dx}px`;
      }
      this.x = e.clientX;
      this.y = e.clientY;
    },

    pinRgPopupToScreen() {
      const popupContainer = this.$refs.radarGridObjectPopupContainer;
      const boundingClientRect = popupContainer.getBoundingClientRect();
      this.styleObject = {
        top: `${boundingClientRect.y}px`,
        left: `${boundingClientRect.x}px`,
        position: "fixed",
      };
      document.getElementById(`radar-grid-layer`).append(popupContainer);
    },

    mouseUpHandler() {
      document.removeEventListener("mousemove", this.mouseMoveHandler);
      document.removeEventListener("mouseup", this.mouseUpHandler);
    },

    onClickTrackBtn: _.throttle(function() {
      this.$emit("onClickTrackBtn", this.coordinates);
    }, 5000),

    onClickCloseBtn: _.throttle(function() {
      this.$emit("onClickCloseBtn", this.objectId);
    }, 1000),

    onClickFeedbackStatusBtn(nextRiskLevel) {
      if (!this.hasWriteRateRiskPrivilege) return;
      this.tmpSelectRiskLevel = nextRiskLevel;
    },

    handleAcceptUpdateRiskLevel: _.debounce(
      async function(nextRiskLevel, options = {}) {
        try {
          this.resetTmpSelectRiskLevel();
          this.$vs.loading({
            type: "border",
            container: `#${this.elId}  .rg-risk-level-content.vs-con-loading__container`,
            scale: 0.4,
          });
          const prevRiskLevel = this.riskLevel;
          this.riskLevel = await this.updateRiskLevel(nextRiskLevel, options);
          if (prevRiskLevel !== this.riskLevel) {
            this.$emit(
              "onClickFeedbackStatusBtn",
              this.objectId,
              this.riskLevel
            );
          }
        } catch (e) {
          this.$vs.notify({
            title: "危険性の評価",
            text: "危険性の評価に失敗しました",
            color: "danger",
            position: "top-center",
          });
        } finally {
          this.$nextTick(() => {
            this.$vs.loading.close(
              `#${this.elId}  .rg-risk-level-content.rg-risk-level-content > .con-vs-loading`
            );
          });
        }
      },
      500,
      { leading: true, trailing: false }
    ),

    handleClickPinButton: _.debounce(
      function() {
        this.pinRgPopupToScreen();
      },
      300,
      { leading: true, trailing: false }
    ),

    getImgUrlLoaded() {
      return this.src;
    },

    transferPopupReactiveValues() {
      return {
        trackNumber: this.trackNumber,
        riskLevel: this.riskLevel,
        //aiseaCameraName: this.aiseaCameraName,
      };
    },

    blinkBackground(elId) {
      let el = document.getElementById(elId);
      el.classList.add("blink-background");
      setTimeout(() => {
        el.classList.remove("blink-background");
      }, 3000);
    },

    async updateRiskLevel(nextRiskLevel, options = {}) {
      const res = await AiseaApiFactory.get("radarGridRiskAssessments").create(
        this.initializeRequestParamPostRadarGridRiskAssessments(
          this.objectId,
          nextRiskLevel,
          options
        ).createPayload()
      );
      return this.initializeResponseCreateRadarGridRiskAssessments(
        res
      ).getRiskLevel();
    },

    resetTmpSelectRiskLevel() {
      this.$nextTick(() => {
        this.tmpSelectRiskLevel = null;
      });
    },

    showTooltip(type) {
      this.tooltip = type;
    },

    hideTooltip() {
      this.tooltip = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

@mixin flex-centering() {
  display: flex;
  justify-content: center;
  align-items: center;
}

.radar-grid-object-popup-container {
  button {
    // reset button style
    all: unset;
    outline: revert;
  }
  border-color: $aisea-gray;
  border-width: 1px;
  border-style: solid;
  padding: 9px;
  &.is-pinned-to-screen {
    border: none;
    padding: 10px;
  }
  z-index: 11;
  display: grid;
  width: max-content;
  height: 200px !important;
  grid-template-areas:
    "rg-img rg-info"
    "rg-feb  rg-info";
  grid-template-rows: 145px 1fr;
  grid-template-columns: 240px 1fr;
  background: #021431;
  border-radius: 5px;
  cursor: move;
  user-select: none;
  padding-right: 30px;

  .close-btn {
    display: block;
    position: absolute;
    top: 4px;
    right: 4px;
    text-align: right;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #222222;
    cursor: pointer;
    user-select: none;

    &:hover {
      background-color: #333333;
      transition: background-color 0.3s ease;
      cursor: pointer;
    }
  }

  &.is-pinned-to-screen {
    .close-btn {
      top: 5px;
      right: 5px;
    }
  }

  .close-btn::before,
  .close-btn::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2px;
    height: 12px;
    background: #fff;
  }

  .close-btn::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .close-btn::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  .rg-pin-icon {
    background-image: url("~@/assets/images/radarGrid/push_pin_default.svg");
    background-repeat: no-repeat;
    background-position: center;

    position: absolute;
    top: 33px;
    right: 4px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    cursor: pointer;
    user-select: none;
    &:hover {
      transition: background-image 0.3s ease;
      background-image: url("~@/assets/images/radarGrid/push_pin_hover.svg");
    }
  }

  &.is-pinned-to-screen {
    .rg-pin-icon {
      display: none;
    }
  }

  .rg-img {
    grid-area: rg-img;
    background-color: #021431;
    border-radius: 5px;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      border-radius: 5px;
      user-drag: none;
      -webkit-user-drag: none;
      -moz-user-select: none;
    }

    /** 画像がないメッセージを表示する */

    $rg-img-controls-column-gap: 5px;
    .rg-img-controls {
      position: absolute;
      bottom: $rg-img-controls-column-gap;
      left: 1px + $rg-img-controls-column-gap;
      right: 1px + $rg-img-controls-column-gap;
      display: grid;
      grid-auto-flow: column; // grid-template-rows: auto; + grid-template-columns: repeat(auto-fill, auto);　で横に配置するには必要
      grid-template-rows: auto;
      grid-template-columns: repeat(auto-fill, auto);
      justify-content: left;
      grid-column-gap: $rg-img-controls-column-gap;
      pointer-events: none;

      & > * {
        @include flex-centering;
        width: 24px;
        height: 24px;
        background-color: #222222;
        border-radius: 50vh;

        &:hover {
          background-color: #333333;
          transition: background-color 0.3s ease;
          cursor: pointer;
        }

        img {
          // border-radiusが継承されているかのような適用になるケースがある
          // 継承されないはずなので、要否を再検証
          border-radius: 0;
          pointer-events: auto;
        }
      }

      .rg-zoom-pic-btn,
      .rg-video-btn {
        img {
          width: 14px;
          height: 14px;
        }
      }
    }
  }

  .rg-img {
    .no-image-text-wrap,
    .rg-img-error {
      @include flex-centering;
      .no-image-text,
      .rg-img-error-text {
        color: #fff;
      }
    }
  }

  .rg-info {
    grid-area: rg-info;
    background-color: #021431;
    padding-left: 14px;

    $rg-info-content-row-height: 17px;
    $rg-info-content-row-header-width: 50px;
    .rg-info-content {
      display: grid;
      grid-template-areas:
        "rg-info-recognition-rate"
        "rg-info-speed"
        "rg-info-direction"
        "rg-info-latitude"
        "rg-info-longitude"
        "rg-info-date"
        "rg-info-camera"
        "rg-info-auto-assessment";
      grid-template-rows: $rg-info-content-row-height;
      row-gap: 9px;

      .rg-info-content-row {
        display: flex;
        align-items: center;
        column-gap: 6px;
        height: $rg-info-content-row-height;
      }

      .rg-info-content-header {
        font-size: 12px;
        color: $aisea-mouse-gray;
        font-family: $aisea-font-family;
        font-weight: 400;
        width: $rg-info-content-row-header-width;
        height: $rg-info-content-row-height;
      }

      .rg-info-recognition-rate {
        grid-area: rg-info-recognition-rate;
      }

      .rg-info-speed {
        grid-area: rg-info-speed;
      }

      .rg-info-direction {
        grid-area: rg-info-direction;
      }

      .rg-info-latitude {
        grid-area: rg-info-latitude;
      }

      .rg-info-longitude {
        grid-area: rg-info-longitude;
      }

      .rg-info-date {
        grid-area: rg-info-date;
      }

      .rg-info-camera {
        grid-area: rg-info-camera;
      }

      .rg-info-auto-assessment {
        grid-area: rg-info-auto-assessment;
      }
    }
  }

  .rg-feb {
    grid-area: rg-feb;
    display: grid;
    grid-template-areas: "rg-track-content rg-feb-content";
    grid-template-rows: 44px 1fr;
    grid-template-columns: 1fr 132px;

    .rg-track-content {
      grid-area: rg-track-content;
      display: grid;
      grid-template-areas: "track-ic track-number";
      grid-template-columns: 24px 1fr;
      gap: 5px;

      .track-ic {
        grid-area: track-ic;
        align-self: center;
        justify-self: center;
        width: 28px;
        height: 28px;

        img {
          display: block;
          margin: auto;

          path {
            color: #f00;
            fill: currentColor;
          }
        }

        &:hover {
          cursor: pointer;
        }
      }

      .track-number {
        grid-area: track-number;
        align-self: center;
        justify-self: start;
        font-weight: 500;
      }
    }

    .rg-risk-level-content {
      grid-area: rg-feb-content;
      align-self: center;
      justify-self: center;
      display: grid;
      align-items: center;
      grid-template-areas: "rg-feb-cancel rg-feb-status-danger rg-feb-status-caution rg-feb-status-safe";
      grid-template-rows: auto;
      grid-template-columns: 22px;
      height: 28px;
      border-radius: 28px;
      column-gap: 10px;
      padding: 0 0.5rem;

      .rg-feb-cancel {
        grid-area: rg-feb-cancel;

        span,
        button {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0 auto;
          font-size: 14px;
          font-weight: 600;

          &:hover {
            cursor: pointer;
          }
        }
      }

      .rg-feb-status-caution {
        grid-area: rg-feb-status-caution;

        span,
        button {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0 auto;
          font-size: 14px;
          font-weight: 600;

          &:hover {
            cursor: pointer;
            &:disabled,
            &.selected-risk-level {
              cursor: not-allowed;
            }
          }
        }
        &.can-be-used {
          &:hover {
            opacity: 1 !important;
          }
        }
      }

      .rg-feb-status-danger {
        grid-area: rg-feb-status-danger;

        span,
        button {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0 auto;
          font-size: 14px;
          font-weight: 600;

          &:hover {
            cursor: pointer;
            &:disabled,
            &.selected-risk-level {
              cursor: not-allowed;
            }
          }
        }
        &.can-be-used {
          &:hover {
            opacity: 1 !important;
          }
        }
      }

      .rg-feb-status-safe {
        grid-area: rg-feb-status-safe;

        span,
        button {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 0 auto;
          font-size: 14px;
          font-weight: 600;

          &:hover {
            cursor: pointer;
            &:disabled,
            &.selected-risk-level {
              cursor: not-allowed;
            }
          }
        }
        &.can-be-used {
          &:hover {
            opacity: 1 !important;
          }
        }
      }
    }
  }

  .loader-wrap {
    @include flex-centering;
  }

  .loader-wrap,
  .no-image-text-wrap,
  .rg-img-error {
    background-color: #222222;
    border-radius: 5px;
  }

  .loader {
    border: 4px solid #b0b0b0;
    border-top: 4px solid #f3f3f3;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    animation: spin 1s linear infinite;
  }

  @keyframes flash {
    0% {
      background-color: #fff2;
      box-shadow: 16px 0 #fff2, -16px 0 #fff;
    }
    50% {
      background-color: #fff;
      box-shadow: 16px 0 #fff2, -16px 0 #fff2;
    }
    100% {
      background-color: #fff2;
      box-shadow: 16px 0 #fff, -16px 0 #fff2;
    }
  }

  .blink-background {
    animation: bgBlinkAnime 1000ms;
    animation-iteration-count: 2;
  }

  @keyframes bgBlinkAnime {
    0% {
      background: #f2f2f2;
    }
    100% {
      background: #8e8e8e;
    }
  }

  // 監視状態を外れた時に操作を制限する
  &.unmonitored {
    .rg-img {
      pointer-events: none;
    }
    .rg-feb {
      pointer-events: none;
    }
    .rg-img-controls {
      pointer-events: none;
      & > * {
        img {
          pointer-events: none;
        }
      }
    }
  }
}

.rg-risk-level-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rg-feb-status-danger img,
.rg-feb-status-caution img,
.rg-feb-status-safe img {
  width: 28px;
  height: 28px;
}

.risk-label {
  font-size: 14px;
  margin-left: 8px;
  font-weight: 500;
}

.rg-tooltip {
  position: absolute;
  top: -32px;
  left: 50%;
  transform: translateX(-50%);
  background: #1b1919;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
  text-align: center;
}
.rg-feb-status-danger,
.rg-feb-status-caution,
.rg-feb-status-safe {
  position: relative;
}

.icon-stack {
  position: relative;
  width: 28px;
  height: 28px;
  display: inline-block;
  flex-shrink: 0;
}

.icon-stack .bg-icon {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.icon-stack .overlay-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 18px;
  height: 18px;
  transform: translate(-50%, -50%);
  object-fit: contain;
  pointer-events: none;
}
</style>
